import axios from 'axios';
import { baseUrl } from './config';

// What the signed-in user is allowed to do, as decided by Role Master and narrowed, per screen,
// by that user's Screen Access grid in User Master.
//
// Held in memory for the life of the page and NOT in localStorage. It was stored there once, so
// that a screen could ask `can('delete')` while it rendered without waiting on a request -- but a
// stored answer is a stale answer: enable a screen in Screen Master, reload, and the browser
// still had yesterday's copy saying no. Permissions are re-read on every navigation instead, so
// a change in the masters shows up on the next screen the user opens.
//
// The backend checks the role on every request regardless -- hiding a button is presentation, not
// permission. The per-screen narrowing is administrative: it can only take away what the role
// already grants, never add.
//
// See backend/utils/roles.js for the operation list and backend/utils/access.js for the layering.

const SUPERADMIN_ROLE = '0';

// The rule the code used before Role Master existed. Used whenever there is nothing loaded to
// read -- the request failed, or the migrations have not been run. Denying instead would take
// Disable away from every admin the moment they upgraded, without anyone having decided that.
const fallbackOperations = (role) => {
    if (String(role) === SUPERADMIN_ROLE) return ['view', 'create', 'update', 'active', 'inactive', 'disable', 'delete'];
    if (String(role) === '1') return ['view', 'create', 'update', 'active', 'inactive', 'disable'];
    return ['view', 'create', 'update'];
};

// Module-level, deliberately. Cleared on logout so the next person to sign in on this browser
// does not inherit it.
let loaded = null;

// ---------------------------------------------------------------------------
// The session copy
//
// This used to be re-read from the server on EVERY navigation, and the answer costs five queries
// -- the user, their screen-access overrides, their role's operations, every screen, and which
// departments hold which. Opening ten screens asked the same question ten times and got the same
// answer ten times.
//
// It is kept in sessionStorage now, with the time it was fetched, and re-read when that is older
// than the window below. sessionStorage rather than a cookie: the payload is 6.5 KB for a
// superadmin, past the ~4 KB a cookie holds, and a cookie would be sent up with every request
// besides. Per tab rather than shared, so closing the tab is also a way to be sure of a fresh one.
//
// The cost is staleness, and it is worth saying out loud: a screen enabled in Screen Master, or a
// department changed in User Master, reaches somebody already signed in when their copy expires,
// not the moment it is saved. Three things cut that short -- signing out, closing the tab, and
// saving any of the masters that shape permissions, which clears the copy through the response
// hook in comman/http.js.
const CACHE_KEY = 'permissions:v1';
const CACHE_WINDOW_MS = 15 * 60 * 1000;

// Storage can throw -- private windows, and browsers set to block site data -- and permissions
// must not fail because a cache could not be read.
const readCache = () => {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const entry = JSON.parse(raw);
        const sameUser = String(entry?.userId ?? '') === String(localStorage.getItem('userId') ?? '');
        const fresh = Date.now() - Number(entry?.fetchedAt ?? 0) < CACHE_WINDOW_MS;
        return sameUser && fresh ? entry.data : null;
    } catch (error) {
        return null;
    }
};

const writeCache = (data) => {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({
            userId: localStorage.getItem('userId') ?? '',
            fetchedAt: Date.now(),
            data,
        }));
    } catch (error) {
        // A cache that cannot be written is a cache that is not used. Nothing else changes.
    }
};

const clearPermissions = () => {
    loaded = null;
    try {
        sessionStorage.removeItem(CACHE_KEY);
    } catch (error) {
        // Nothing to clear, or nowhere to clear it from.
    }
};

// What the user may do, from memory if this page has already asked, from the session copy if it
// is still inside the window, and from the server otherwise.
const refreshPermissions = async ({ force = false } = {}) => {
    const role = localStorage.getItem('userRole');
    const userId = localStorage.getItem('userId');
    if (role === null && userId === null) {
        clearPermissions();
        return null;
    }

    if (!force) {
        if (loaded !== null) return loaded;
        const cached = readCache();
        if (cached !== null) {
            loaded = cached;
            return loaded;
        }
    }

    try {
        // Per user where we know who they are. A session that signed in before User Master
        // shipped has no stored id, and the role-level answer is the best that can be had.
        const url = userId
            ? `${baseUrl}/user_master/permissions?user_id=${userId}`
            : `${baseUrl}/role_master/permissions`;
        const response = await axios.get(url);
        const payload = response.data?.data ?? {};
        loaded = Array.isArray(payload)
            ? { role_operations: payload, screens: {}, inactive: [] }
            : {
                role_operations: payload.role_operations ?? [],
                screens: payload.screens ?? {},
                inactive: payload.inactive ?? [],
            };
        writeCache(loaded);
    } catch (error) {
        // Null, not empty: empty would read as "allowed nothing" and shut the user out of the
        // whole portal because a request failed. Null falls through to the rule above.
        console.warn('[permissions] Could not read permissions; falling back to the role rule.', error);
        loaded = null;
    }
    return loaded;
};

// The operations the user has on one screen path. Falls back to the role's own operations for a
// path that is not a screen in Screen Master -- an Add or Edit page, say, which hangs off a list
// screen rather than being one.
const operationsFor = (path) => {
    const role = localStorage.getItem('userRole');
    // The superadmin holds every operation and is always active, whatever the masters say --
    // the same floor the backend applies. It is the account that fixes a permissions mistake.
    if (String(role) === SUPERADMIN_ROLE) return fallbackOperations(SUPERADMIN_ROLE);

    const permissions = loaded;
    if (permissions === null) return fallbackOperations(role);

    const screen = permissions.screens?.[path];
    if (Array.isArray(screen)) return screen;
    return permissions.role_operations ?? [];
};

// May the signed-in user perform this operation on this screen? One of the values in
// backend/utils/roles.js: view, create, update, active, inactive, disable, delete.
//
// The path defaults to the page being rendered, which is what makes the Screen Access grid mean
// something: the same role can hold Disable and still not have it on this particular screen.
const can = (operation, path) => {
    const target = path ?? (typeof window !== 'undefined' ? window.location.pathname : '');
    return operationsFor(target).includes(operation);
};

const currentOperations = (path) => operationsFor(path ?? (typeof window !== 'undefined' ? window.location.pathname : ''));

// Is this screen view-only for the signed-in user -- may they open it and change nothing on it?
//
// The screen path is required rather than defaulted, because the screens that ask are exactly
// the ones whose own path is not a screen: /receipe/view/12 and /user_master/edit/3 are pages
// hanging off /receipe/list and /user_master/list, and asking about the current path would fall
// back to the role's operations and lose the per-screen narrowing that decided this in the first
// place.
//
// Create as well as Update, because a screen with neither has nothing to write: Add is a Create
// and Save is an Update, and a user holding one of them is not view-only.
const isViewOnly = (screenPath) => {
    const operations = operationsFor(screenPath);
    return !operations.includes('create') && !operations.includes('update');
};

// ---------------------------------------------------------------------------
// Route access
// ---------------------------------------------------------------------------

const firstSegment = (path) => String(path ?? '').replace(/^\//, '').split('/')[0];

// Which operation a route needs. A route that IS a screen needs only View -- it is a destination
// in somebody's menu, and being allowed to open what you were given is the whole point. The
// routes that hang off a screen are the Add and Edit pages, and those are Create and Update.
const requiredOperation = (routePath, isScreenItself) => {
    if (isScreenItself) return 'view';
    if (/\/add$/.test(routePath)) return 'create';
    if (/\/edit\//.test(routePath)) return 'update';
    return 'view';
};

// May the signed-in user open this route?
//
// Three answers, because there are three cases and they are not the same:
//   allowed                 -- open it
//   { reason: 'inactive' }  -- the screen has been taken out of service in Screen Master
//   { reason: 'operation' } -- the screen is live but this user does not have the operation
//
// A route that is not a screen and does not hang off one -- /login, and the handful never put in
// the menu -- is left alone: there is nothing on it to check, and nothing to check it against.
// Likewise when there are no stored permissions at all, which is a session that signed in before
// this shipped or a deploy that has not run the migrations.
const accessToPath = (routePath) => {
    const permissions = loaded;
    if (permissions === null) return { allowed: true };

    const screens = permissions.screens ?? {};
    const inactive = permissions.inactive ?? [];

    if (Object.prototype.hasOwnProperty.call(screens, routePath)) {
        return screens[routePath].includes('view')
            ? { allowed: true }
            : { allowed: false, reason: 'operation', operation: 'view' };
    }
    // An inactive screen is nobody's, the superadmin included -- the same rule the side bar
    // applies, where a disabled screen is dropped before any role is considered.
    if (inactive.includes(routePath)) return { allowed: false, reason: 'inactive' };

    // Not a screen itself. Find the screens it hangs off, by the first path segment:
    // /session_master/add belongs to /session_master/list.
    const segment = firstSegment(routePath);
    const activeKin = Object.keys(screens).filter((path) => firstSegment(path) === segment);
    const inactiveKin = inactive.filter((path) => firstSegment(path) === segment);

    if (activeKin.length === 0 && inactiveKin.length === 0) return { allowed: true };
    if (activeKin.length === 0) return { allowed: false, reason: 'inactive' };

    // More than one screen can share a segment (/menu_creation/view and /menu_creation/menu_edit),
    // so having the operation on any of them is enough -- they are one screen family.
    const operation = requiredOperation(routePath, false);
    const permitted = activeKin.some((path) => screens[path].includes(operation));
    return permitted ? { allowed: true } : { allowed: false, reason: 'operation', operation };
};

export { can, currentOperations, isViewOnly, accessToPath, refreshPermissions, clearPermissions };
